import React from "react";
import { Box, TextField } from "@mui/material";

const FIELD_STYLES = {
    text: {
        borderBottom: "1px solid #000",
        fontFamily: "DM Sans",
        fontSize: "14px",
        paddingBottom: "2px",
    },
    textarea: {
        "& .MuiOutlinedInput-root": {
            fontFamily: "DM Sans",
            fontSize: "14px",
        },
    },
    label: {
        fontFamily: "DM Sans",
        fontSize: "14px",
    },
};

const FieldRenderer = ({
    field,
    sectionIndex,
    fieldIndex,
    formData,
    otherTextValues,
    handleInputChange,
    handleCheckboxChange,
    handleOtherTextChange,
}) => {
    const key = `section_${sectionIndex}_field_${fieldIndex}`;

    switch (field.type) {
        case "text":
        case "email":
        case "url":
        case "date":
            return (
                <TextField
                    fullWidth
                    type={field.type}
                    variant="standard"
                    value={formData[key] || ""}
                    onChange={(e) => handleInputChange(key, e.target.value)}
                    placeholder={field.placeholder}
                    disabled={field.disabled}
                    InputProps={{
                        disableUnderline: false,
                        sx: FIELD_STYLES.text,
                    }}
                />
            );

        case "textarea":
            return (
                <TextField
                    fullWidth
                    multiline
                    rows={field.rows || 3}
                    variant="outlined"
                    value={formData[key] || ""}
                    onChange={(e) => handleInputChange(key, e.target.value)}
                    placeholder={field.placeholder}
                    disabled={field.disabled}
                    sx={FIELD_STYLES.textarea}
                />
            );

        case "file": {
            const fileName = formData[key] || "";
            return (
                <div>
                    <input
                        type="file"
                        multiple={field.multiple}
                        accept={field.accept}
                        onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            const fileNames = files.map((file) => file.name);
                            handleInputChange(
                                key,
                                field.multiple ? fileNames : fileNames[0] || ""
                            );
                        }}
                    />
                    {fileName && (
                        <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
                            Selected File
                            {field.multiple && Array.isArray(fileName) ? "s" : ""}:{" "}
                            {Array.isArray(fileName) ? fileName.join(", ") : fileName}
                        </div>
                    )}
                </div>
            );
        }

        case "checkbox":
            return (
                <Box>
                    {field.options?.map((option, optionIndex) => {
                        const isOtherOption =
                            option.toLowerCase().includes("other") ||
                            option.toLowerCase().includes("specify");
                        const checkedValues = formData[key] || [];
                        const isChecked = isOtherOption
                            ? checkedValues.some((v) => v.startsWith("Other:"))
                            : checkedValues.includes(option);
                        const otherKey = `${key}_otherText`;

                        return (
                            <Box
                                key={optionIndex}
                                display="flex"
                                alignItems="center"
                                mb={1}
                                sx={{ gap: 1 }}
                            >
                                <input
                                    type="checkbox"
                                    id={`${key}_${optionIndex}`}
                                    checked={isChecked}
                                    disabled={field.disabled}
                                    onChange={(e) => {
                                        if (field.disabled) return;
                                        if (isOtherOption && !e.target.checked) {
                                            handleOtherTextChange(
                                                sectionIndex,
                                                fieldIndex,
                                                optionIndex,
                                                ""
                                            );
                                        }
                                        handleCheckboxChange(
                                            sectionIndex,
                                            fieldIndex,
                                            optionIndex,
                                            e.target.checked,
                                            option
                                        );
                                    }}
                                    style={{
                                        marginRight: 8,
                                        cursor: field.disabled ? "not-allowed" : "pointer",
                                    }}
                                />
                                <label
                                    htmlFor={`${key}_${optionIndex}`}
                                    style={{
                                        ...FIELD_STYLES.label,
                                        flex: 1,
                                        color: field.disabled ? "#999" : "inherit",
                                        cursor: field.disabled ? "not-allowed" : "pointer",
                                    }}
                                >
                                    {option
                                        .split("(specify")[0]
                                        .split("(please specify")[0]
                                        .trim()}
                                </label>

                                {isOtherOption && !field.disabled && (
                                    <TextField
                                        variant="standard"
                                        placeholder="Specify..."
                                        value={otherTextValues[otherKey] || ""}
                                        onChange={(e) => {
                                            if (!checkedValues.some((v) => v.startsWith("Other:"))) {
                                                handleCheckboxChange(
                                                    sectionIndex,
                                                    fieldIndex,
                                                    optionIndex,
                                                    true,
                                                    option
                                                );
                                            }
                                            handleOtherTextChange(
                                                sectionIndex,
                                                fieldIndex,
                                                optionIndex,
                                                e.target.value
                                            );
                                        }}
                                        sx={{ ...FIELD_STYLES.label, ml: 1, width: "180px" }}
                                    />
                                )}
                            </Box>
                        );
                    })}
                </Box>
            );

        default:
            return (
                <TextField
                    fullWidth
                    type={field.type}
                    variant="standard"
                    value={formData[key] || ""}
                    onChange={(e) => handleInputChange(key, e.target.value)}
                    placeholder={field.placeholder}
                    disabled={field.disabled}
                    InputProps={{
                        disableUnderline: false,
                        sx: FIELD_STYLES.text,
                    }}
                />
            );
    }
};

export default FieldRenderer;
